import { HttpClient, HttpErrorResponse, HttpEventType, HttpHeaders, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError, TimeoutError } from 'rxjs';
import { catchError, last, map, switchMap, tap, timeout } from 'rxjs/operators';
import { ConflictValidationException, HttpException, NetworkException, TimeoutHttpException } from '../utils/exceptions';
import { HttpStatusCode } from '../utils/http/http-status-code';
import { HttpHelper } from '../utils/http/http-helper';
import { GlobalConstants } from '../constant/constant';
import { BlobStorageEndpoints } from '../constant/API-endpoints-constants';
import { BlobType } from '../enums/blob-storage/blob-type.enum';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private BLOB_STORATE_CHUNK_SIZE = GlobalConstants.MAX_BLOB_STORAGE_CHUNK_SIZE;
  constructor(private http: HttpClient) {}

  private getEventMessage(event: any, file: File): string {
    if (!event) {
      return '';
    }
    switch (event.type) {
      case (HttpEventType.Sent, HttpEventType.UploadProgress):
        const fileSize = HttpHelper.formatBytes(file.size);
        const uploadedSize = HttpHelper.formatBytes(event.loaded);
        return `${uploadedSize}/${fileSize} uploaded`;

      case HttpEventType.Response:
        return 'Upload complete';

      default:
        return 'Upload in progress';
    }
  }

  private handleError(e: any): Observable<never> {
    let error;
    if (e instanceof ProgressEvent) {
      error = new NetworkException('Request failed due to network error.');
    } else if (e instanceof HttpErrorResponse) {
      switch (e.status) {
        case HttpStatusCode.CONNECTION_REFUSED:
          error = new NetworkException('Server Error: Connection refused.');
          break;
        case HttpStatusCode.CONFLICT:
          error = new ConflictValidationException('Server Error: ' + JSON.stringify(e.error ?? e.message));
          break;
        default:
          error = new HttpException('Server Error: ' + JSON.stringify(e.error ?? e.message));
          break;
      }
    } else if (e instanceof TimeoutError) {
      error = new TimeoutHttpException('Server Error: Request timed out.');
    } else {
      error = new Error('An unknown error occurred');
    }
    return throwError(error);
  }

  public request<T>(method: string, url: string, data?: any, timeoutInSeconds?: number, options?: any) {
    let httpRequest: Observable<T>;

    const headers = options && options.headers ? options.headers : new HttpHeaders();
    const params = options && options.params ? options.params : new HttpParams();
    const body = data ? JSON.stringify(data) : null;

    httpRequest = this.http.request<T>(method, url, { body, headers, params });

    if (timeoutInSeconds) {
      httpRequest = httpRequest.pipe(timeout(timeoutInSeconds * 1000));
    }

    return httpRequest.pipe(catchError(this.handleError));
  }

  public get<T>(url: string, headers?: HttpHeaders): Observable<HttpResponse<T>> {
    return this.http.get<T>(url, { observe: 'response', headers }).pipe(catchError(this.handleError.bind(this)));
  }

  public getOnlyBody<T>(url: string, headers?: HttpHeaders): Observable<T> {
    return this.http.get<T>(url, { headers }).pipe(catchError(this.handleError.bind(this)));
  }

  public getBlob(url: string, headers?: HttpHeaders): Observable<Blob> {
    return this.http.get(url, { headers, responseType: 'blob' }).pipe(catchError(this.handleError.bind(this)));
  }

  public post<T>(url: string, data: any, headers?: HttpHeaders): Observable<T> {
    const body = JSON.stringify(data);
    return this.http.post<T>(url, body, { headers }).pipe(catchError(this.handleError.bind(this)));
  }

  public postBlob(url: string, data: any, headers?: HttpHeaders): Observable<Blob> {
    return this.http.post(url, data, { headers, responseType: 'blob' }).pipe(catchError(this.handleError.bind(this)));
  }

  public put<T>(url: string, data: any, headers?: HttpHeaders): Observable<T> {
    const body = JSON.stringify(data);
    return this.http.put<T>(url, body, { headers }).pipe(catchError(this.handleError.bind(this)));
  }

  public putBlob<T>(url: string, data: any, onProgress?: (message: string) => void, headers?: HttpHeaders): Observable<T> {
    const params: HttpParams = new HttpParams().set('timeout', GlobalConstants.MAX_BLOB_STORAGE_REQUEST_TIMEOUT_SECONDS.toString());
    const req = new HttpRequest('PUT', url, data, { headers, reportProgress: true, params });
    return this.http.request<T>(req).pipe(
      timeout(GlobalConstants.MAX_BLOB_STORAGE_REQUEST_TIMEOUT_SECONDS * 1000),
      map(event => this.getEventMessage(event, data)),
      tap(message => onProgress(message)),
      last(),
      catchError(this.handleError.bind(this))
    );
  }

  public patch<T>(url: string, data: any, headers?: HttpHeaders): Observable<T> {
    const body = JSON.stringify(data);
    return this.http.patch<T>(url, body, { headers }).pipe(catchError(this.handleError.bind(this)));
  }

  public delete<T>(url: string, headers?: HttpHeaders): Observable<T> {
    return this.http.delete<T>(url, { headers }).pipe(catchError(this.handleError.bind(this)));
  }

  public upload(url: string, data: FormData, headers?: HttpHeaders): Observable<any> {
    return this.http.post(url, data, { headers }).pipe(catchError(this.handleError.bind(this)));
  }

  public putBlobInChunks<T>(createUrl: string, filename: string, data: File, type: BlobType.INDEXED_DB | BlobType.VIDEO, onProgress?: (message: string) => void, headers?: HttpHeaders): Observable<T> {
    const totalBlocks = Math.ceil(data.size / this.BLOB_STORATE_CHUNK_SIZE);
    const blockIds: string[] = [];

    for (let i = 0; i < totalBlocks; i++) {
      blockIds.push(this.getBlockId(i));
    }

    return this.uploadBlocks(data, createUrl, blockIds,  0, onProgress).pipe(
      switchMap(() => this.commitBlockList(filename, blockIds, type)),
      catchError(this.handleError.bind(this))
    );
  }

  /**
   * Uploads the blocks of a file to the blob storage.
   * @param file - The file to upload.
   * @param url - The URL of the blob storage.
   * @param blockIds - The list of block IDs.
   * @param index - The index of the block to upload.
   * @param onProgress - A callback to report the progress of the upload.
   * @returns An observable that completes when the blocks are uploaded.
   */
  private uploadBlocks(file: File, url: string, blockIds: string[], index: number, onProgress?: (message: string) => void): Observable<any> {
    if (index >= blockIds.length) {
      return of('Upload complete');
    }

    const start = index * this.BLOB_STORATE_CHUNK_SIZE;
    const end = Math.min(start + this.BLOB_STORATE_CHUNK_SIZE, file.size);
    const block = file.slice(start, end);
    const blockId = blockIds[index];

    const headers = new HttpHeaders({
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': 'application/octet-stream',
    });

    if (onProgress) {
      onProgress(`Uploading file (${HttpHelper.formatBytes(file.size)})...`);
    }

    return this.http.put(`${url}&comp=block&blockid=${blockId}`, block, { headers, reportProgress: true }).pipe(
      tap(event => {
        const uploadedSize = HttpHelper.formatBytes(start + block.size);
        const totalSize = HttpHelper.formatBytes(file.size);

        if (onProgress) {
          onProgress(`${uploadedSize}/${totalSize} uploaded`);
        }
      }),
      last(),
      switchMap(() => this.uploadBlocks(file, url, blockIds, index + 1, onProgress))
    );
  }

  /**
   * Commits the block list to the blob storage.
   * @param filename - The name of the file.
   * @param blockIds - The list of block IDs.
   * @returns An observable that completes when the block list is committed.
   */
  private commitBlockList(filename: string, blockIds: string[], type: BlobType.INDEXED_DB | BlobType.VIDEO): Observable<any> {
    const requestUrl = type === BlobType.INDEXED_DB ? `${BlobStorageEndpoints[type].write}?filename=${filename}` : this.buildUrl(BlobStorageEndpoints[type].write, { filename });
    return this.http.get<{ UriToken: string; FileName: string }>(requestUrl).pipe(
      switchMap(tokenResponse => {
        const url = tokenResponse.UriToken;
        const headers = new HttpHeaders({
          'Request-Type': 'commit',
          'x-ms-date': new Date().toString(),
          'x-ms-version': '2020-04-08',
          'Content-Type': 'text/plain; charset=UTF-8',
        });

        const blockListXml = `<?xml version="1.0" encoding="utf-8"?><BlockList>${blockIds.map(id => `<Uncommitted>${id}</Uncommitted>`).join('')}</BlockList>`;

        return this.http.put(`${url}&comp=blocklist`, blockListXml, { headers });
      })
    );
  }

  private getBlockId(index: number): string {
    return btoa(index.toString().padStart(6, '0'));
  }

  /**
   * Builds a URL by substituting placeholders in the path and appending additional properties as query parameters.
   * @param urlTemplate - The URL template with placeholders for substitution. Placeholders are enclosed in curly braces.
   * @param params - The parameters used for substitution and query parameters.
   * @returns The built URL.
   */
  public buildUrl(urlTemplate: string, params: Record<string, any>): string {
    const paramsCopy = { ...params };

    // Substitute placeholders in the path
    let url = urlTemplate.replace(/{(\w+)}/g, (match, key) => {
      const value = paramsCopy[key];
      delete paramsCopy[key];
      return encodeURIComponent(value) || match;
    });

    // Append additional properties as query parameters
    const queryParams = new URLSearchParams();
    for (const [key, value] of Object.entries(paramsCopy)) {
      if (value !== null) {
        queryParams.append(key, value.toString());
      }
    }
    const queryString = queryParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }

    return url;
  }
}
